import { TextField, InputAdornment, Box, IconButton, createTheme, ThemeProvider, CssBaseline, Backdrop, CircularProgress, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { createContext, forwardRef, useState, useRef, useEffect, useMemo, useContext } from 'react';
import { useMediaQuery, useLocalStorage } from '@tiles-erp/hooks';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { isoToDateField, maskDateField, dateFieldToIso } from '@tiles-erp/shared';

// src/theme/palette.ts
var brand = {
  primary: "#3A57E8",
  primaryDark: "#2438B8",
  primaryWash: "#EAEDFD",
  secondary: "#5A6474",
  success: "#1AA053",
  warning: "#F16A1B",
  error: "#C03221",
  info: "#079AA2"
};
var wash = {
  light: {
    primary: "#EAEDFD",
    success: "#E3F5EB",
    warning: "#FEEDE1",
    error: "#FBE7E4",
    info: "#E1F4F5",
    neutral: "#F1F3F9"
  },
  dark: {
    primary: "#23294A",
    success: "#17301F",
    warning: "#35251A",
    error: "#351D1B",
    info: "#122A2C",
    neutral: "#212533"
  }
};
var shadows = {
  light: {
    sm: "0 1px 2px rgba(35,45,66,.06)",
    md: "0 4px 16px rgba(35,45,66,.07)",
    lg: "0 12px 32px rgba(35,45,66,.10)"
  },
  dark: {
    sm: "0 1px 2px rgba(0,0,0,.4)",
    md: "0 4px 16px rgba(0,0,0,.45)",
    lg: "0 12px 32px rgba(0,0,0,.55)"
  }
};
var getWash = (mode) => wash[mode];
var getShadows = (mode) => shadows[mode];
var getPalette = (mode) => mode === "light" ? {
  mode,
  primary: {
    main: brand.primary,
    dark: brand.primaryDark,
    light: wash.light.primary,
    contrastText: "#FFFFFF"
  },
  secondary: { main: brand.secondary },
  success: { main: brand.success, light: wash.light.success },
  warning: { main: brand.warning, light: wash.light.warning },
  error: { main: brand.error, light: wash.light.error },
  info: { main: brand.info, light: wash.light.info },
  // The page ground is a cool grey, so white cards lift off it without needing
  // a border. That difference is what carries the whole card treatment.
  background: { default: "#F1F3F9", paper: "#FFFFFF" },
  text: { primary: "#232D42", secondary: "#5A6474", disabled: "#8A92A6" },
  divider: "#E9EBF3",
  action: { hover: "#F1F3F9", selected: "#EAEDFD" }
} : {
  mode,
  primary: {
    main: "#6E85F5",
    dark: "#4A63E7",
    light: wash.dark.primary,
    contrastText: "#0F1117"
  },
  secondary: { main: "#8990A3" },
  success: { main: "#43C47D", light: wash.dark.success },
  warning: { main: "#F6924A", light: wash.dark.warning },
  error: { main: "#E4695A", light: wash.dark.error },
  info: { main: "#3FBFC6", light: wash.dark.info },
  background: { default: "#14161F", paper: "#1B1E2A" },
  text: { primary: "#E7EAF3", secondary: "#B2B9CA", disabled: "#8990A3" },
  divider: "#2B3040",
  action: { hover: "#212533", selected: "#262C48" }
};
var createAppTheme = (mode) => {
  const shadow = getShadows(mode);
  const isLight = mode === "light";
  const base = createTheme({ palette: getPalette(mode) });
  const { divider, background, text } = base.palette;
  const display = "Plus Jakarta Sans, Inter, sans-serif";
  return createTheme(base, {
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: ["Inter", "Roboto", "Segoe UI", "system-ui", "sans-serif"].join(","),
      // Headings use Plus Jakarta Sans for a little more character in titles and
      // figures; body and controls stay on Inter, which is built for small sizes.
      h1: { fontFamily: display, fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-0.4px" },
      h2: { fontFamily: display, fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.3px" },
      h3: { fontFamily: display, fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.2px" },
      h4: { fontFamily: display, fontSize: "1.05rem", fontWeight: 700 },
      h5: { fontFamily: display, fontSize: "0.95rem", fontWeight: 700 },
      h6: { fontFamily: display, fontSize: "0.875rem", fontWeight: 700 },
      subtitle2: { fontSize: "0.8rem", fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
      body2: { fontSize: "0.825rem" },
      caption: { fontSize: "0.72rem" },
      overline: { fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.09em" }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Digits that sit in columns must not jitter as values change.
          ".num, td, th": { fontVariantNumeric: "tabular-nums" },
          // Numeric fields use typed values; browser spinner buttons are too easy to
          // hit accidentally and differ between Chrome and Firefox.
          'input[type="number"]': { MozAppearance: "textfield" },
          'input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button': {
            WebkitAppearance: "none",
            margin: 0
          },
          "*::-webkit-scrollbar": { width: 8, height: 8 },
          "*::-webkit-scrollbar-thumb": {
            background: isLight ? "#DFE2EE" : "#363C4F",
            borderRadius: 4
          },
          [base.breakpoints.down("sm")]: {
            // Filter toolbars across the ERP were designed as compact desktop rows.
            // On a phone each control needs the available width and the row must wrap.
            "main .MuiStack-root:has(> .MuiTextField-root), main .MuiStack-root:has(> .MuiAutocomplete-root), .MuiDialogContent-root .MuiStack-root:has(> .MuiTextField-root), .MuiDialogContent-root .MuiStack-root:has(> .MuiAutocomplete-root)": {
              flexWrap: "wrap",
              minWidth: 0
            },
            "main .MuiStack-root > .MuiTextField-root, main .MuiStack-root > .MuiAutocomplete-root, .MuiDialogContent-root .MuiStack-root > .MuiTextField-root, .MuiDialogContent-root .MuiStack-root > .MuiAutocomplete-root": {
              width: "100% !important",
              minWidth: "0 !important",
              maxWidth: "none !important",
              flex: "1 1 100%"
            },
            // Native tables remain useful in detail dialogs; horizontal scrolling keeps
            // their columns readable instead of squeezing every value into a few pixels.
            ".MuiDialogContent-root .MuiTableContainer-root": {
              maxWidth: "100%",
              overflowX: "auto"
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          // MUI's default elevation ladder is far heavier than this design wants.
          root: { backgroundImage: "none" },
          elevation1: { boxShadow: shadow.md }
        }
      },
      MuiCard: {
        // Elevated, not outlined — the change that defines the new look.
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: shadow.md, border: "none" }
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: "17px 20px", borderBottom: `1px solid ${divider}` },
          title: { fontFamily: display, fontSize: "0.95rem", fontWeight: 700 },
          subheader: { fontSize: "0.75rem" }
        }
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: "16px 20px", "&:last-child": { paddingBottom: 20 } } }
      },
      MuiButton: {
        defaultProps: { disableElevation: true, size: "small" },
        styleOverrides: {
          root: { borderRadius: 9, padding: "7px 15px" },
          containedPrimary: {
            boxShadow: isLight ? "0 4px 12px rgba(58,87,232,.28)" : "none",
            "&:hover": { boxShadow: isLight ? "0 6px 16px rgba(58,87,232,.32)" : "none" }
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          fullWidth: true,
          // Suppress the browser's own name/address/phone autofill, which otherwise
          // covers in-app suggestion lists. Fields that genuinely want it (login)
          // set their own autoComplete value, which takes precedence.
          autoComplete: "off"
        }
      },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 9 } } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: { background: background.paper, borderBottom: `1px solid ${divider}` }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { border: "none", borderRight: `1px solid ${divider}`, backgroundImage: "none" }
        }
      },
      MuiIconButton: {
        defaultProps: { size: "small" },
        styleOverrides: { root: { borderRadius: 9 } }
      },
      MuiChip: {
        defaultProps: { size: "small" },
        styleOverrides: {
          root: { borderRadius: 20, fontWeight: 600, fontSize: "0.72rem" },
          // Soft-filled status pills rather than solid blocks of colour.
          filled: { border: "none" }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 64, "@media (min-width:600px)": { minHeight: 64 } }
        }
      },
      MuiListItemButton: {
        styleOverrides: { root: { paddingTop: 6, paddingBottom: 6, borderRadius: 8 } }
      },
      MuiTable: { defaultProps: { size: "small" } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              background: background.default,
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: text.disabled,
              borderBottom: "none",
              whiteSpace: "nowrap"
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${divider}`, padding: "10px 16px" }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: shadow.lg,
            [base.breakpoints.down("sm")]: {
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
              borderRadius: 0
            }
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: display,
            fontWeight: 700,
            fontSize: "1.05rem",
            [base.breakpoints.down("sm")]: { padding: "16px", fontSize: "1.15rem" }
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: 12,
            paddingBottom: 12,
            [base.breakpoints.down("sm")]: {
              padding: "12px 16px 88px",
              overflowX: "hidden"
            }
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            [base.breakpoints.down("sm")]: {
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              gap: 8,
              padding: "8px 12px max(8px, env(safe-area-inset-bottom))",
              borderTop: `1px solid ${divider}`,
              background: background.paper,
              "& > :not(style)": {
                flex: "1 1 0",
                minHeight: 48,
                margin: 0,
                fontSize: "0.92rem"
              }
            }
          }
        }
      },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12, boxShadow: shadow.lg } } },
      MuiTooltip: {
        styleOverrides: { tooltip: { borderRadius: 8, fontSize: "0.72rem", padding: "6px 10px" } }
      },
      MuiTablePagination: {
        styleOverrides: { toolbar: { minHeight: 44 }, root: { fontSize: "0.8rem" } }
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } }
    }
  });
};
var ColorModeContext = createContext(void 0);
function ColorModeProvider({
  children,
  storageKey = "tiles-erp:color-mode"
}) {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useLocalStorage(storageKey, prefersDark ? "dark" : "light");
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode((prev) => prev === "light" ? "dark" : "light")
    }),
    [mode, setMode]
  );
  return /* @__PURE__ */ jsx(ColorModeContext.Provider, { value, children: /* @__PURE__ */ jsxs(ThemeProvider, { theme, children: [
    /* @__PURE__ */ jsx(CssBaseline, {}),
    children
  ] }) });
}
function useColorMode() {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error("useColorMode must be used within a ColorModeProvider");
  return ctx;
}
function LoadingOverlay({ open }) {
  return /* @__PURE__ */ jsx(Backdrop, { open, sx: { zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }, children: /* @__PURE__ */ jsx(CircularProgress, { color: "inherit" }) });
}
function PageContainer({
  title,
  subtitle,
  actions,
  children
}) {
  return /* @__PURE__ */ jsxs(Box, { sx: { p: { xs: 1, md: 1.5 } }, children: [
    /* @__PURE__ */ jsxs(
      Stack,
      {
        direction: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        spacing: 1,
        sx: { mb: 1.5 },
        children: [
          /* @__PURE__ */ jsxs(Box, { children: [
            /* @__PURE__ */ jsx(Typography, { variant: "h6", lineHeight: 1.2, children: title }),
            subtitle && /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: { xs: "none", sm: "block" } }, children: subtitle })
          ] }),
          actions && /* @__PURE__ */ jsx(Box, { sx: { width: { xs: "100%", sm: "auto" } }, children: actions })
        ]
      }
    ),
    children
  ] });
}
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel
}) {
  return /* @__PURE__ */ jsxs(Dialog, { open, onClose: onCancel, maxWidth: "xs", fullWidth: true, children: [
    /* @__PURE__ */ jsx(DialogTitle, { children: title }),
    /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsx(DialogContentText, { children: message }) }),
    /* @__PURE__ */ jsxs(DialogActions, { children: [
      /* @__PURE__ */ jsx(Button, { onClick: onCancel, color: "inherit", children: cancelLabel }),
      /* @__PURE__ */ jsx(Button, { onClick: onConfirm, color: destructive ? "error" : "primary", variant: "contained", children: confirmLabel })
    ] })
  ] });
}
var FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"])',
  "select",
  "textarea",
  '[role="combobox"]'
].join(", ");
var isTypeable = (element) => {
  const tag = element.tagName.toLowerCase();
  if (tag === "input") {
    const type = element.type;
    return type !== "checkbox" && type !== "radio" && type !== "file";
  }
  return tag === "select" || tag === "textarea" || element.getAttribute("role") === "combobox";
};
var isReachable = (element) => !element.hasAttribute("disabled") && element.getAttribute("aria-disabled") !== "true" && element.getAttribute("aria-hidden") !== "true" && element.tabIndex !== -1 && element.getClientRects().length > 0;
function EnterKeyNavigation() {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Enter") return;
      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.defaultPrevented || event.isComposing) return;
      const target = event.target;
      if (!target || target.isContentEditable) return;
      if (target.tagName.toLowerCase() === "textarea") return;
      if (!target.matches(FIELD_SELECTOR) || !isTypeable(target)) return;
      if (target.getAttribute("aria-expanded") === "true") return;
      const scope = target.closest('[role="dialog"]') ?? document.body;
      const fields = Array.from(scope.querySelectorAll(FIELD_SELECTOR)).filter(
        (element) => isTypeable(element) && isReachable(element)
      );
      const position = fields.indexOf(target);
      const next = position === -1 ? void 0 : fields[position + 1];
      if (!next) return;
      event.preventDefault();
      next.focus();
      if (next instanceof HTMLInputElement && next.type !== "date" && next.type !== "time") {
        next.select();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  return null;
}
var registrations = [];
function useSaveShortcut(handler, enabled = true) {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!enabled) return void 0;
    const registration = { invoke: () => latest.current() };
    registrations.push(registration);
    return () => {
      const index = registrations.indexOf(registration);
      if (index !== -1) registrations.splice(index, 1);
    };
  }, [enabled]);
}
function SaveShortcut() {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() !== "s") return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      const current = registrations[registrations.length - 1];
      if (!current) return;
      event.preventDefault();
      current.invoke();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  return null;
}
var DateField = forwardRef(function DateField2(props, ref) {
  const { value, defaultValue, type = "date", onChange, inputRef, InputProps, inputProps, helperText, error, ...rest } = props;
  const withTime = type === "datetime-local";
  const initial = String(value ?? defaultValue ?? "");
  const [draft, setDraft] = useState(() => isoToDateField(initial, withTime));
  const [localIso, setLocalIso] = useState(initial);
  const [invalid, setInvalid] = useState(false);
  const lastEmitted = useRef(null);
  const native = useRef(null);
  const text = useRef(null);
  const currentIso = String(value ?? localIso ?? "");
  useEffect(() => {
    if (value !== void 0 && String(value) !== lastEmitted.current) {
      setDraft(isoToDateField(String(value ?? ""), withTime));
      setInvalid(false);
      text.current?.setCustomValidity("");
    }
  }, [value, withTime]);
  const format = withTime ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY";
  const change = (event, display, iso) => {
    const min = inputProps?.min;
    const max = inputProps?.max;
    const outOfRange = Boolean(iso && (min && iso < min || max && iso > max));
    const bad = Boolean(display && (iso === null || outOfRange));
    setDraft(display);
    setInvalid(bad);
    text.current?.setCustomValidity(bad ? `Enter a valid ${format} date${min || max ? " within the allowed range" : ""}.` : "");
    const next = bad ? "" : iso ?? "";
    lastEmitted.current = next;
    setLocalIso(next);
    onChange?.({ ...event, target: { ...event.target, name: props.name ?? "", value: next }, currentTarget: { ...event.currentTarget, name: props.name ?? "", value: next } });
  };
  return /* @__PURE__ */ jsx(
    TextField,
    {
      ...rest,
      ref,
      type: "text",
      value: draft,
      placeholder: format,
      error: error || invalid,
      helperText: invalid ? `Enter ${format}` : helperText,
      inputRef: (element) => {
        text.current = element;
        if (typeof inputRef === "function") inputRef(element);
        else if (inputRef) inputRef.current = element;
      },
      inputProps: { ...inputProps, inputMode: "numeric", maxLength: withTime ? 16 : 10 },
      onChange: (event) => {
        const display = maskDateField(event.target.value, withTime);
        change(event, display, dateFieldToIso(display, withTime));
      },
      InputProps: { ...InputProps, endAdornment: /* @__PURE__ */ jsxs(Fragment, { children: [
        InputProps?.endAdornment,
        /* @__PURE__ */ jsx(InputAdornment, { position: "end", children: /* @__PURE__ */ jsxs(Box, { sx: { position: "relative", display: "flex" }, children: [
          /* @__PURE__ */ jsx(IconButton, { size: "small", disabled: props.disabled || Boolean(InputProps?.readOnly), "aria-label": props.label ? `Choose ${String(props.label)}` : "Choose date", onClick: () => {
            if (native.current?.showPicker) native.current.showPicker();
            else {
              native.current?.focus();
              native.current?.click();
            }
          }, children: /* @__PURE__ */ jsx(CalendarMonthIcon, { sx: { fontSize: 18 } }) }),
          /* @__PURE__ */ jsx("input", { ref: native, type, value: currentIso, min: inputProps?.min, max: inputProps?.max, disabled: props.disabled, tabIndex: -1, "aria-hidden": "true", style: { position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }, onChange: (event) => change(event, isoToDateField(event.target.value, withTime), event.target.value) })
        ] }) })
      ] }) }
    }
  );
});

export { ColorModeProvider, ConfirmDialog, DateField, EnterKeyNavigation, LoadingOverlay, PageContainer, SaveShortcut, brand, createAppTheme, getPalette, getShadows, getWash, useColorMode, useSaveShortcut };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map